import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { cn } from '@/lib/cn';

// Entrance — the image starts small and far, flies toward the viewer (3D
// translateZ) growing to full-bleed, then splits.
const ENTER_SCALE = 0.46; // starting size (1 = full)
const ENTER_Z = -520; // starting depth in px (more negative = further away)

type SplitRevealProps = {
  src: string;
  alt: string;
  eyebrow?: string;
  /** Statement that sits behind the image, revealed as the halves part. */
  lines: string[];
  caption?: string;
  className?: string;
};

/**
 * Full-bleed pinned scroll moment:
 *  - the photo flies in from depth, small → full-bleed;
 *  - then it splits down the middle, the halves sliding off to the sides,
 *    uncovering a statement that sits behind it.
 *
 * The statement fades in WHILE the image is still full and covering it, so the
 * user only ever sees it revealed by the cut. The fade is tied to the image
 * `scale` (not a raw scroll window) and the section/sticky use the SAME viewport
 * unit (vh) — both are needed so it behaves identically on mobile Safari, where
 * the dynamic toolbar makes svh ≠ vh and broke the timing before.
 */
export function SplitReveal({ src, alt, eyebrow, lines, caption, className }: SplitRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

  // Entrance: small + far → full-bleed via 3D depth, done by 0.4; then split.
  const scale = useTransform(scrollYProgress, [0, 0.4], [ENTER_SCALE, 1]);
  const z = useTransform(scrollYProgress, [0, 0.4], [ENTER_Z, 0]);
  // Statement opacity is driven by the image scale: it reaches full opacity
  // exactly as the image reaches full-bleed (scale 1), then the split uncovers it.
  const statementOpacity = useTransform(scale, [0.85, 0.99], [0, 1]);
  const leftX = useTransform(scrollYProgress, [0.44, 0.9], ['0%', '-101%']);
  const rightX = useTransform(scrollYProgress, [0.44, 0.9], ['0%', '101%']);

  const statementContent = (
    <>
      {eyebrow && <p className="eyebrow mb-8 text-brass">{eyebrow}</p>}
      <h2 className="font-display text-[13vw] leading-[1.02] tracking-tight md:text-[8vw]">
        {lines.map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </h2>
      {caption && <p className="mt-10 max-w-md text-sm leading-relaxed text-stone">{caption}</p>}
    </>
  );

  if (reduce) {
    return (
      <section
        className={cn('relative flex h-screen w-full flex-col items-center justify-center px-6 text-center', className)}
      >
        {statementContent}
      </section>
    );
  }

  return (
    <section ref={ref} className={cn('relative h-[240vh]', className)}>
      <div
        className="sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden bg-paper"
        style={{ perspective: '1200px' }}
      >
        {/* Statement sits behind; fades in under the full image, revealed by the cut */}
        <motion.div
          style={{ opacity: statementOpacity }}
          className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
        >
          {statementContent}
        </motion.div>

        {/* The photo — flies in from depth, then splits to uncover the statement */}
        <motion.div style={{ scale, z }} className="absolute inset-0 will-change-transform">
          <motion.div style={{ x: leftX }} className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">
            <img
              src={src}
              alt={alt}
              decoding="async"
              className="absolute inset-y-0 left-0 h-full w-[200%] max-w-none object-cover"
            />
          </motion.div>
          <motion.div style={{ x: rightX }} className="absolute inset-y-0 right-0 w-1/2 overflow-hidden">
            <img
              src={src}
              alt=""
              aria-hidden
              decoding="async"
              className="absolute inset-y-0 right-0 h-full w-[200%] max-w-none object-cover"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
